import usePersistentValue from './usePersistentValue'
import {useEffect} from 'react'

function addBodyEventListener<K extends keyof HTMLElementEventMap>(type: K, listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => any, options?: boolean | AddEventListenerOptions) {
    document.body.addEventListener(type, listener, options)
    return () => {
        document.body.removeEventListener(type, listener, options)
    }
}

function addBodyEventListeners(types: Array<keyof HTMLElementEventMap> | string, listener: AnyFn, options?: boolean | AddEventListenerOptions) {
    const eventNames = Array.isArray(types) ? types : types.trim().split(/\s+/)
    const removeFuncs = eventNames.map((ev: any) => addBodyEventListener(ev, listener, options))
    return () => {
        for(const r of removeFuncs) {
            r()
        }
    }
}

function some<T>(arr: T | ReadonlyArray<T>, cond: (x: T) => unknown) {
    return Array.isArray(arr) ? arr.some(cond) : Boolean(cond(arr as T))
}

export function useClickOutsideHandler(handler: AnyFn, excludeElements: ReadonlyArray<HTMLElement | null>) {
    const state = usePersistentValue<{ skip: boolean, timer: undefined | number }>({
        skip: false,
        timer: undefined,
    })

    useEffect(() => {
        return addBodyEventListener('click', ev => {
            if(state.skip || some(excludeElements, r => r?.contains(ev.target))) {
                clearTimeout(state.timer)
                state.skip = false
                return
            }
            handler(ev)
        })
    }, [handler, excludeElements, state])

    return () => {
        state.skip = true
        state.timer = window.setTimeout(() => {
            state.skip = false
        })
    }
}
